import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';

export interface Notification {
	id: string;
	type: 'success' | 'error' | 'warning' | 'info';
	title: string;
	message: string;
	duration?: number;
}

interface NotificationContextType {
	notifications: Notification[];
	addNotification: (notification: Omit<Notification, 'id'>) => void;
	removeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
	const context = useContext(NotificationContext);
	if (!context) {
		throw new Error('useNotifications must be used within a NotificationProvider');
	}
	return context;
};

const NotificationItem: React.FC<{
	notification: Notification;
	onRemove: (id: string) => void;
}> = ({ notification, onRemove }) => {
	console.log('[NOTIFICATION_ITEM] Rendering notification:', notification.id, notification.title);
	useEffect(() => {
		const duration = notification.duration ?? 5000;
		console.log(
			'[NOTIFICATION_ITEM] Setting up timer for notification:',
			notification.id,
			'duration:',
			duration,
			'type:',
			typeof duration,
		);

		if (duration > 0) {
			console.log('[NOTIFICATION_ITEM] Creating setTimeout with duration:', duration);
			const timer = setTimeout(() => {
				console.log(
					'[NOTIFICATION_ITEM] Timer fired! Auto-removing notification:',
					notification.id,
					'after',
					duration,
					'ms',
				);
				onRemove(notification.id);
			}, duration);

			console.log('[NOTIFICATION_ITEM] Timer created with ID:', timer);

			return () => {
				console.log('[NOTIFICATION_ITEM] Cleanup timer for notification:', notification.id, 'timer ID:', timer);
				clearTimeout(timer);
			};
		}

		// Return undefined for duration <= 0 case
		return undefined;
	}, [notification.id, notification.duration, onRemove]);

	const getIcon = () => {
		switch (notification.type) {
			case 'success':
				return <CheckCircle className="h-5 w-5 text-green-400" />;
			case 'error':
				return <AlertCircle className="h-5 w-5 text-red-400" />;
			case 'warning':
				return <AlertTriangle className="h-5 w-5 text-yellow-400" />;
			case 'info':
				return <Info className="h-5 w-5 text-blue-400" />;
		}
	};

	const getColors = () => {
		switch (notification.type) {
			case 'success':
				return 'bg-white border-green-200 text-green-800';
			case 'error':
				return 'bg-white border-red-200 text-red-800';
			case 'warning':
				return 'bg-white border-yellow-200 text-yellow-800';
			case 'info':
				return 'bg-white border-blue-200 text-blue-800';
		}
	};

	return (
		<div
			className={`max-w-sm w-full shadow-lg rounded-lg pointer-events-auto border-l-4 ${getColors()} animate-slide-in`}>
			<div className="p-4">
				<div className="flex items-start">
					<div className="flex-shrink-0">{getIcon()}</div>
					<div className="ml-3 w-0 flex-1 pt-0.5">
						<p className="text-sm font-medium">{notification.title}</p>
						<p className="mt-1 text-sm opacity-90">{notification.message}</p>
					</div>
					<div className="ml-4 flex-shrink-0 flex">
						<button
							className="rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
							onClick={() => onRemove(notification.id)}>
							<span className="sr-only">Close</span>
							<X className="h-5 w-5" />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [notifications, setNotifications] = useState<Notification[]>([]);

	const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
		console.log('[NOTIFICATION] addNotification called with:', notification.title);
		// Check for duplicate notifications (same title and message)
		setNotifications((prev) => {
			console.log('[NOTIFICATION] Current notifications count:', prev.length);
			const isDuplicate = prev.some(
				(existing) =>
					existing.title === notification.title &&
					existing.message === notification.message &&
					existing.type === notification.type,
			);

			if (isDuplicate) {
				console.log('[NOTIFICATION] Duplicate notification prevented:', notification.title);
				return prev; // Don't add duplicate
			}

			const id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
			const newNotification = {
				...notification,
				id,
				duration: notification.duration ?? 5000, // Default 5 seconds
			};

			console.log('[NOTIFICATION] Adding new notification:', newNotification.title);
			return [newNotification, ...prev];
		});
	}, []);

	const removeNotification = useCallback((id: string) => {
		console.log('[NOTIFICATION] Removing notification:', id);
		setNotifications((prev) => {
			const filtered = prev.filter((notification) => notification.id !== id);
			console.log('[NOTIFICATION] Notifications after removal:', filtered.length);
			return filtered;
		});
	}, []);

	return (
		<NotificationContext.Provider value={{ notifications, addNotification, removeNotification }}>
			{children}

			{/* Notification Container - Fixed position, top-right */}
			<div
				aria-live="assertive"
				className="fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start z-50">
				<div className="w-full flex flex-col items-center space-y-4 sm:items-end">
					{notifications.map((notification) => (
						<NotificationItem key={notification.id} notification={notification} onRemove={removeNotification} />
					))}
				</div>
			</div>
		</NotificationContext.Provider>
	);
};
